import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import audioPlayer from './audioPlayer.js';

// Enable slideshow debugging by default; override with ?debug=0
try { window.__DEBUG_SLIDESHOW = true; } catch(_) {}
try {
  const params = new URLSearchParams((window.location && window.location.search) || '');
  if (params.get('debug') === '0') { window.__DEBUG_SLIDESHOW = false; }
} catch(_) {}

let SLIDESHOW_MANIFEST = null;
let SLIDESHOW_PROMISE = null;
async function getManifest() {
  if (SLIDESHOW_MANIFEST) return SLIDESHOW_MANIFEST;
  if (!SLIDESHOW_PROMISE) {
    const base = (import.meta && import.meta.env && typeof import.meta.env.BASE_URL === 'string') ? import.meta.env.BASE_URL : '/';
    SLIDESHOW_PROMISE = fetch(`${base}slideshows.json`)
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then(json => { SLIDESHOW_MANIFEST = json || {}; return SLIDESHOW_MANIFEST; });
  }
  return SLIDESHOW_PROMISE;
}

let SLIDESHOW_ORDER_MANIFEST = null;
let SLIDESHOW_ORDER_PROMISE = null;
async function getOrderManifest() {
  if (SLIDESHOW_ORDER_MANIFEST) return SLIDESHOW_ORDER_MANIFEST;
  if (!SLIDESHOW_ORDER_PROMISE) {
    const base = (import.meta && import.meta.env && typeof import.meta.env.BASE_URL === 'string') ? import.meta.env.BASE_URL : '/';
    SLIDESHOW_ORDER_PROMISE = fetch(`${base}slideshows-order.json`, { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then(json => { SLIDESHOW_ORDER_MANIFEST = json || {}; return SLIDESHOW_ORDER_MANIFEST; });
  }
  return SLIDESHOW_ORDER_PROMISE;
}

// Helper: whether to use stacked mobile layout (small screens in portrait)
function shouldUseMobileStack() {
  try {
    const hasMM = !!window.matchMedia;
    const isSmall = hasMM ? window.matchMedia('(max-width: 767px)').matches : ((window.innerWidth || 0) < 768);
    const isPortrait = hasMM ? window.matchMedia('(orientation: portrait)').matches : ((window.innerHeight || 0) >= (window.innerWidth || 0));
    return isSmall && isPortrait;
  } catch(_) { return false; }
}

function pauseAllVideos(root) {
  try { Array.from(root.querySelectorAll('video')).forEach(v => { try { v.pause(); } catch(_) {} }); } catch(_) {}
}

function markVideoInteracted(video) {
  try {
    if (!video) return;
    try { video._hasPlayedOnce = true; } catch(_) {}
    const vp = video.closest('.video-player-container');
    const poster = vp && vp.querySelector('.video-poster');
    if (poster) { try { poster.style.display = 'none'; } catch(_) {} }
    // If inside a Swiper (desktop), propagate to all clones with same real index
    try {
      const slideEl = video.closest('.swiper-slide');
      const modal = slideEl && slideEl.closest && slideEl.closest('.nav-dropdown.slideshow');
      const rawReal = slideEl && slideEl.getAttribute && slideEl.getAttribute('data-swiper-slide-index');
      const realIdx = rawReal != null ? parseInt(rawReal, 10) : NaN;
      if (modal && Number.isFinite(realIdx)) {
        const slides = Array.from(modal.querySelectorAll(`.swiper-slide[data-swiper-slide-index="${realIdx}"]`));
        slides.forEach(s => {
          const vv = s.querySelector('video');
          const pp = s.querySelector('.video-poster');
          if (vv) { try { vv._hasPlayedOnce = true; } catch(_) {} }
          if (pp) { try { pp.style.display = 'none'; } catch(_) {} }
        });
      }
    } catch(_) {}
  } catch(_) {}
}

function resetAllVideos(root) {
  try {
    const slides = Array.from(root.querySelectorAll('.swiper-slide'));
    slides.forEach((s) => {
      const v = s.querySelector('video');
      if (v) {
        try { v.pause(); } catch(_) {}
        try { v.currentTime = 0; } catch(_) {}
        try { v._hasPlayedOnce = false; } catch(_) {}
        const poster = s.querySelector('.video-poster');
        if (poster) { try { poster.style.display = ''; } catch(_) {} }
        const overlay = s.querySelector('.video-overlay');
        if (overlay) { overlay.classList.add('show-play'); overlay.classList.remove('show-pause'); }
      }
    });
  } catch(_) {}
}

async function buildSlides(modalEl) {
  const id = modalEl.getAttribute('data-id');
  const host = modalEl.querySelector('.slideshow');
  const section = modalEl.querySelector('.section-content');
  if (!id || !host) return;
  try {
    if (section) {
      section.style.visibility = 'hidden';
      modalEl.dataset.ready = '';
      section.setAttribute('aria-busy', 'true');
    }
  } catch(_) {}

  const manifest = await getManifest();
  const entries = Array.isArray(manifest[id]) ? manifest[id] : [];
  let sources = entries.map(e => {
    if (e && e.video) return { kind: 'video', src: e.video, poster: e.poster || e.img };
    if (e && e.img) return { kind: 'image', src: e.img };
    return null;
  }).filter(Boolean);

  // Apply optional order override from slideshows-order.json
  try {
    const orderManifest = await getOrderManifest();
    const order = Array.isArray(orderManifest[id]) ? orderManifest[id] : null;
    if (order && order.length) {
      const bySrc = new Map();
      sources.forEach(s => { if (s && s.src) bySrc.set(s.src, s); });
      const ordered = [];
      order.forEach(src => { const item = bySrc.get(src); if (item) { ordered.push(item); bySrc.delete(src); } });
      // Append any remaining items not specified in the override, preserving original order
      const leftovers = sources.filter(s => bySrc.has(s.src));
      sources = ordered.concat(leftovers);
    }
  } catch(_) {}

  // Helper: resume score audio if no slideshow videos are playing.
  // On iOS, also bind a one-time user-gesture fallback (touch/click)
  const resumeScoreAfterFullscreenIfIdle = () => {
    try {
      const anyPlaying = Array.from(document.querySelectorAll('.video-player-container video')).some(vv => {
        try { return !vv.paused && !vv.ended; } catch(_) { return false; }
      });
      try { logSS && logSS('RESUME_CHECK', { anyPlaying: anyPlaying ? 1 : 0 }); } catch(_) {}
      if (!anyPlaying) {
        try { audioPlayer.resumeFromSlideshow(); logSS && logSS('AUDIO_RESUME'); } catch(_) {}
        // iOS fallback: if resume is blocked by media policy, resume on next user gesture
        try {
          const ua = navigator.userAgent || '';
          const isiOS = /iP(hone|ad|od)/i.test(ua);
          if (isiOS && !window.__resumeScoreOnNextGesture) {
            const once = () => {
              try { audioPlayer.resumeFromSlideshow(); logSS && logSS('AUDIO_RESUME'); } catch(_) {}
              try { document.removeEventListener('touchend', once, true); } catch(_) {}
              try { document.removeEventListener('click', once, true); } catch(_) {}
              try { window.__resumeScoreOnNextGesture = null; } catch(_) {}
              try { logSS && logSS('IOS_FALLBACK_TAP'); } catch(_) {}
            };
            try { window.__resumeScoreOnNextGesture = true; } catch(_) {}
            try { document.addEventListener('touchend', once, true); } catch(_) {}
            try { document.addEventListener('click', once, true); } catch(_) {}
            try { logSS && logSS('IOS_FALLBACK_BOUND'); } catch(_) {}
            // Auto-clean after a short window to avoid lingering listeners
            setTimeout(() => {
              try {
                if (window.__resumeScoreOnNextGesture) {
                  document.removeEventListener('touchend', once, true);
                  document.removeEventListener('click', once, true);
                  window.__resumeScoreOnNextGesture = null;
                  try { logSS && logSS('IOS_FALLBACK_CLEAN'); } catch(_) {}
                }
              } catch(_) {}
            }, 5000);
          }
        } catch(_) {}
      }
    } catch(_) {}
  };

  // Mobile portrait: stack slides vertically; mobile landscape uses horizontal swiper
  const isMobileStack = shouldUseMobileStack();
  if (isMobileStack) {
    host.innerHTML = '';
    try {
      host.style.overflowY = 'auto';
      host.style.webkitOverflowScrolling = 'touch';
      host.style.height = '';
    } catch(_) {}
    sources.forEach((item, idx) => {
      const slideWrap = document.createElement('div');
      slideWrap.className = 'stack-slide';
      // Mobile margins: 5px between slides, 5px left/right (wider)
      try { slideWrap.style.margin = '5px 5px'; } catch(_) {}
      if (item.kind === 'image') {
        const isSvg = /\.svg(\?|#|$)/i.test(item.src || '');
        if (isSvg) {
          const pad = document.createElement('div');
          pad.className = 'svg-padded';
          const img = document.createElement('img');
          img.className = 'slide';
          img.src = item.src;
          img.alt = '';
          img.style.display = 'block';
          img.style.width = '100%';
          img.style.height = 'auto';
          img.style.objectFit = 'contain';
          pad.appendChild(img);
          // Wrap Best Picture #06 SVG in link to YouTube
          if ((item.src || '').indexOf('/slideshows/best-picture-06.svg') !== -1) {
            const a = document.createElement('a');
            a.href = 'https://www.youtube.com/watch?v=CEQcnXrOJ7w';
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.className = 'slide-link-wrapper';
            a.setAttribute('aria-label', 'Open video on YouTube (new tab)');
            a.style.display = 'block';
            a.appendChild(pad);
            slideWrap.appendChild(a);
          } else {
            slideWrap.appendChild(pad);
          }
        } else {
          const img = document.createElement('img');
          img.className = 'slide';
          img.src = item.src;
          img.alt = '';
          img.style.display = 'block';
          img.style.width = '100%';
          img.style.height = 'auto';
          img.style.objectFit = 'contain';
          // Wrap specific slide in a link (Best Adapted Screenplay #05)
          if ((item.src || '').indexOf('/slideshows/best-adapted-screenplay-05.jpg') !== -1) {
            const a = document.createElement('a');
            a.href = 'https://film.netflixawards.com/scripts/frankenstein';
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.className = 'slide-link-wrapper';
            a.setAttribute('aria-label', 'Open Frankenstein screenplay (new tab)');
            a.style.display = 'block';
            a.appendChild(img);
            slideWrap.appendChild(a);
          } else {
            slideWrap.appendChild(img);
          }
        }
      } else {
        const vp = document.createElement('div');
        vp.className = 'slide video-player-container';
        const v = document.createElement('video');
        v.playsInline = true; v.setAttribute('playsinline',''); v.setAttribute('webkit-playsinline','');
        v.controls = false; v.preload = 'metadata'; v.muted = false; v.src = item.src;
        v.style.display = 'block'; v.style.width = '100%'; v.style.height = 'auto'; v.style.objectFit = 'contain';
        if (item.poster) { try { v.poster = item.poster; } catch(_) {} }
        vp.appendChild(v);
        if (item.poster) {
          const poster = document.createElement('img');
          poster.className = 'video-poster';
          poster.src = item.poster; poster.alt = '';
          poster.style.display = '';
          poster.style.width = '100%'; poster.style.height = '100%'; poster.style.objectFit = 'contain';
          vp.appendChild(poster);
        }
        const fsBtn = document.createElement('button');
        fsBtn.className = 'video-fs';
        fsBtn.setAttribute('aria-label', 'Enter fullscreen');
        fsBtn.innerHTML = '<img src="/img/video-expand.svg" alt="">';
        // Desktop only: fullscreen button hidden/unused on mobile
        // (In mobile stacked layout we rely on tap-to-fullscreen when paused)
        // vp.appendChild(fsBtn);
        const overlay = document.createElement('div');
        overlay.className = 'video-overlay show-play';
        const inner = document.createElement('div'); inner.className = 'overlay-inner';
        const playIcon = document.createElement('img');
        playIcon.className = 'video-play';
        playIcon.alt = '';
        // Special-case: use lower-opacity play icon for specific slides
        try {
          const s = item && item.src ? String(item.src) : '';
          const useAltPlayIcon = /best-picture-07\.mp4|best-adapted-screenplay-08\.mp4/i.test(s);
          playIcon.src = useAltPlayIcon ? '/img/click-to-watch-60-opacity.svg' : '/img/click-to-watch.svg';
        } catch(_) { playIcon.src = '/img/click-to-watch.svg'; }
        const pauseIcon = document.createElement('img'); pauseIcon.className = 'video-pause'; pauseIcon.src = '/img/video-pause.svg'; pauseIcon.alt = '';
        inner.appendChild(playIcon); inner.appendChild(pauseIcon); overlay.appendChild(inner); vp.appendChild(overlay);
        slideWrap.appendChild(vp);
        // Basic interaction: toggle play/pause or enter fullscreen
        vp.addEventListener('click', (e) => {
          const video = vp.querySelector('video');
          try {
            if (e.target && typeof e.target.closest === 'function' && e.target.closest('.video-fs')) {
              // Pause others, assure play, then enter fullscreen
              try {
                const all = Array.from(host.querySelectorAll('video'));
                all.forEach(v => { if (v !== video) { try { v.pause(); } catch(_) {} } });
              } catch(_) {}
              try { audioPlayer.pauseForSlideshow(); } catch(_) {}
              try { markVideoInteracted(video); } catch(_) {}
              try { video.play().catch(()=>{}); } catch(_) {}
              if (typeof video.requestFullscreen === 'function') video.requestFullscreen().catch(()=>{});
              else if (typeof video.webkitEnterFullscreen === 'function') video.webkitEnterFullscreen();
              return;
            }
            if (video.paused) {
              // On mobile: enter fullscreen and start playback on any tap when paused
              // Also mark background score for resume after exit
              try { audioPlayer.pauseForSlideshow(); } catch(_) {}
              try { markVideoInteracted(video); } catch(_) {}
              try {
                if (typeof video.requestFullscreen === 'function') { video.requestFullscreen().catch(()=>{}); }
                else if (typeof video.webkitEnterFullscreen === 'function') { video.webkitEnterFullscreen(); }
              } catch(_) {}
              // Pause other videos in this modal before playing
              try {
                const all = Array.from(host.querySelectorAll('video'));
                all.forEach(v => { if (v !== video) { try { v.pause(); } catch(_) {} } });
              } catch(_) {}
              try { video.play().catch(()=>{}); } catch(_) {}
              overlay.classList.remove('show-play');
              overlay.classList.remove('show-pause');
            } else {
              video.pause();
              overlay.classList.add('show-play');
              overlay.classList.remove('show-pause');
            }
          } catch(_) {}
        });
        // iOS fullscreen events to keep UI in sync on mobile stacked
        try {
          const sync = () => {
            try {
              const posterEl = vp.querySelector('.video-poster');
              const overlayEl = vp.querySelector('.video-overlay');
              if (posterEl) {
                const hidePoster = !!v._hasPlayedOnce || !v.paused;
                posterEl.style.display = hidePoster ? 'none' : '';
              }
              if (overlayEl) {
                if (v.paused) overlayEl.classList.add('show-play');
                else overlayEl.classList.remove('show-play', 'show-pause');
              }
            } catch(_) {}
          };
          v.addEventListener('webkitbeginfullscreen', (e) => {
            try { sync(); } catch(_) {}
            // Ensure score is paused and marked for resume on fullscreen entry
            try { audioPlayer.pauseForSlideshow(); } catch(_) {}
            // Mark video as played and hide poster permanently post-interaction
            try { markVideoInteracted(v); } catch(_) {}
            try { document.body.classList.add('fs-playing'); if (window.__updateRotateMessage) window.__updateRotateMessage(); } catch(_) {}
          });
          v.addEventListener('webkitendfullscreen', (e) => {
            try { sync(); } catch(_) {}
            // Ensure the slideshow video is paused after exiting native fullscreen
            try { v.pause(); } catch(_) {}
            // iOS Safari sometimes fails to render paused frame; force it
            try { ensurePausedFrameRenders(v); } catch(_) {}
            // Ensure the slide remains visible if layout collapsed
            try { ensureVisibleMediaAfterFS(v); } catch(_) {}
            try { logSS && logSS('IOS_FS_END'); } catch(_) {}
            try { resumeScoreAfterFullscreenIfIdle(); } catch(_) {}
            try { document.body.classList.remove('fs-playing'); if (window.__updateRotateMessage) window.__updateRotateMessage(); } catch(_) {}
          });
        } catch(_) {}
      }
      host.appendChild(slideWrap);
      // Add extra space only after the final slide to indicate the end
      if (idx === sources.length - 1) {
        // Previously 24px; increase by 40px for clearer end cue
        try { slideWrap.style.marginBottom = '64px'; } catch(_) {}
      }
    });
    // Wait for images/videos to be ready before showing content to prevent shifts
    const waitStackReady = () => new Promise((resolve) => {
      const imgs = Array.from(host.querySelectorAll('img'));
      const vids = Array.from(host.querySelectorAll('video'));
      let remaining = 0;
      const done = () => { if (remaining <= 0) resolve(); };
      const watchImage = (img) => {
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) return;
        remaining++;
        img.addEventListener('load', () => { remaining--; done(); }, { once: true });
        img.addEventListener('error', () => { remaining--; done(); }, { once: true });
      };
      const watchVideo = (vid) => {
        try { if (vid.readyState >= 1) return; } catch(_) {}
        remaining++;
        vid.addEventListener('loadedmetadata', () => { remaining--; done(); }, { once: true });
        vid.addEventListener('error', () => { remaining--; done(); }, { once: true });
      };
      imgs.forEach(watchImage);
      vids.forEach(watchVideo);
      setTimeout(() => resolve(), 800);
      done();
    });
    await waitStackReady();
    try {
      if (section) {
        section.style.visibility = 'visible';
        section.removeAttribute('aria-busy');
      }
      modalEl.dataset.ready = '1';
      modalEl.dataset.built = '1';
      try { modalEl._mobileStack = true; } catch(_) {}
    } catch(_) {}
    return;
  }

  host.innerHTML = '';
  const swiperEl = document.createElement('div');
  swiperEl.className = 'swiper';
  const wrapperEl = document.createElement('div');
  wrapperEl.className = 'swiper-wrapper';
  swiperEl.appendChild(wrapperEl);
  host.appendChild(swiperEl);

  sources.forEach((item) => {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    if (item.kind === 'image') {
      const isSvg = /\.svg(\?|#|$)/i.test(item.src || '');
      if (isSvg) {
        const pad = document.createElement('div');
        pad.className = 'svg-padded';
        const img = document.createElement('img');
        img.className = 'slide';
        img.src = item.src;
        img.alt = '';
        pad.appendChild(img);
        // Wrap Best Picture #06 SVG in link to YouTube
        if ((item.src || '').indexOf('/slideshows/best-picture-06.svg') !== -1) {
          const a = document.createElement('a');
          a.href = 'https://www.youtube.com/watch?v=CEQcnXrOJ7w';
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.className = 'slide-link-wrapper';
          a.setAttribute('aria-label', 'Open video on YouTube (new tab)');
          a.style.display = 'block';
          a.appendChild(pad);
          slide.appendChild(a);
        } else {
          slide.appendChild(pad);
        }
      } else {
        const img = document.createElement('img');
        img.className = 'slide';
        img.src = item.src;
        img.alt = '';
        // Costume slideshow: avoid cropping by using contain and slide-height sizing
        if (id === 'best-costume-design') {
          try {
            img.style.objectFit = 'contain';
            img.style.height = '100%';
            img.style.width = 'auto';
          } catch(_) {}
        }
        // Wrap specific slide in a link (Best Adapted Screenplay #05)
        if ((item.src || '').indexOf('/slideshows/best-adapted-screenplay-05.jpg') !== -1) {
          const a = document.createElement('a');
          a.href = 'https://film.netflixawards.com/scripts/frankenstein';
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.className = 'slide-link-wrapper';
          a.setAttribute('aria-label', 'Open Frankenstein screenplay (new tab)');
          a.style.display = 'block';
          a.appendChild(img);
          slide.appendChild(a);
        } else {
          slide.appendChild(img);
        }
      }
    } else {
      // Mark video slides for CSS targeting (Safari width tuning)
      try { slide.classList.add('has-video'); } catch(_) {}
      const vp = document.createElement('div');
      vp.className = 'slide video-player-container';
      const v = document.createElement('video');
      v.playsInline = true; v.setAttribute('playsinline',''); v.setAttribute('webkit-playsinline','');
      v.controls = false; v.preload = 'metadata'; v.muted = false; v.src = item.src;
      if (item.poster) { try { v.poster = item.poster; } catch(_) {} }
      const poster = document.createElement('img');
      poster.className = 'video-poster';
      if (item.poster) poster.src = item.poster;
      poster.alt = '';
      vp.appendChild(v);
      if (item.poster) vp.appendChild(poster);
      // Fullscreen button for videos
      const fsBtn = document.createElement('button');
      fsBtn.className = 'video-fs';
      fsBtn.setAttribute('aria-label', 'Enter fullscreen');
      fsBtn.innerHTML = '<img src="/img/video-expand.svg" alt="">';
      vp.appendChild(fsBtn);
      // Initial positioning of fullscreen button; update on media load
      try {
        requestAnimationFrame(() => { try { positionFullscreenButtonFor(vp); } catch(_) {} });
        v.addEventListener('loadedmetadata', () => { try { positionFullscreenButtonFor(vp); } catch(_) {} });
        v.addEventListener('loadeddata', () => { try { positionFullscreenButtonFor(vp); } catch(_) {} });
        if (poster) { poster.addEventListener('load', () => { try { positionFullscreenButtonFor(vp); } catch(_) {} }); }
      } catch(_) {}
      // Play/Pause overlay centered over video
      const overlay = document.createElement('div');
      overlay.className = 'video-overlay show-play';
      const inner = document.createElement('div');
      inner.className = 'overlay-inner';
      const playIcon = document.createElement('img');
      playIcon.className = 'video-play';
      playIcon.alt = '';
      // Special-case: use lower-opacity play icon for specific slides
      try {
        const s = item && item.src ? String(item.src) : '';
        const useAltPlayIcon = /best-picture-07\.mp4|best-adapted-screenplay-08\.mp4/i.test(s);
        playIcon.src = useAltPlayIcon ? '/img/click-to-watch-60-opacity.svg' : '/img/click-to-watch.svg';
      } catch(_) { playIcon.src = '/img/click-to-watch.svg'; }
      const pauseIcon = document.createElement('img');
      pauseIcon.className = 'video-pause';
      pauseIcon.src = '/img/video-pause.svg';
      pauseIcon.alt = '';
      inner.appendChild(playIcon);
      inner.appendChild(pauseIcon);
      overlay.appendChild(inner);
      vp.appendChild(overlay);
      // iOS fullscreen events to keep UI in sync
      try {
        const sync = () => {
          try {
            const posterEl = vp.querySelector('.video-poster');
            const overlayEl = vp.querySelector('.video-overlay');
            if (posterEl) {
              const hidePoster = !!v._hasPlayedOnce || !v.paused;
              posterEl.style.display = hidePoster ? 'none' : '';
            }
            if (overlayEl) {
              if (v.paused) overlayEl.classList.add('show-play');
              else overlayEl.classList.remove('show-play', 'show-pause');
            }
          } catch(_) {}
        };
        v.addEventListener('webkitbeginfullscreen', (e) => {
          try {
            sync();
            const modal = vp.closest('.nav-dropdown.slideshow');
            if (modal) { window.__fsActiveModal = modal; }
            try { document.body.classList.add('fs-playing'); if (window.__updateRotateMessage) window.__updateRotateMessage(); } catch(_) {}
            // Mark entry time for fullscreen to distinguish auto-pauses
            try { if (!window.__fsEntryAt) window.__fsEntryAt = new WeakMap(); window.__fsEntryAt.set(v, Date.now()); } catch(_) {}
            // Also store the real slide index at FS entry on iOS
            try {
              // Prefer the slide's attribute, then fallback to swiper.realIndex
              let idx = null;
              const slide = vp.closest('.swiper-slide');
              const raw = slide && slide.getAttribute && slide.getAttribute('data-swiper-slide-index');
              if (raw != null) idx = parseInt(raw, 10);
              if (!Number.isFinite(idx)) {
                const sw = modal && modal._swiper;
                if (sw && typeof sw.realIndex === 'number') idx = sw.realIndex;
              }
              if (modal && Number.isFinite(idx)) modal._fsTargetIndex = idx;
              // Capture exact duplicate index at entry to restore same clone later
              try {
                const sw = modal && modal._swiper;
                if (sw && typeof sw.activeIndex === 'number') modal._fsActiveIndexDup = sw.activeIndex;
              } catch(_) {}
              try {
                if (window.__DEBUG_SLIDESHOW) {
                  const sw = modal && modal._swiper;
                  console.debug('[SS] FS open (iOS begin)', {
                    modal: modal && modal.getAttribute && modal.getAttribute('data-id'),
                    raw,
                    storedIdx: idx,
                    swiperReal: sw && sw.realIndex,
                    swiperActive: sw && sw.activeIndex
                  });
                }
              } catch(_) {}
            } catch(_) {}
            try { setMustPlayGuard(v, 3000); scheduleForcePlay(v); } catch(_) {}
            try { window.__fsActiveVideo = v; } catch(_) {}
          } catch(_) {}
        });
        v.addEventListener('webkitendfullscreen', (e) => {
          try {
            sync();
            const modal = vp.closest('.nav-dropdown.slideshow');
            const sw = modal && modal._swiper;
            let idx = null;
            if (modal) {
              if (typeof modal._fsTargetIndex === 'number') idx = modal._fsTargetIndex;
              else if (typeof modal._activeSlideIndex === 'number') idx = modal._activeSlideIndex;
            }
            if (sw && idx !== null) {
              restoreSwiperToIndex(sw, modal, idx);
              // Extra settle handled inside restore
            }
            try { if (modal) modal._fsTargetIndex = null; } catch(_) {}
            if (window.__fsActiveModal === modal) window.__fsActiveModal = null;
            try { if (window.__fsMustPlay) window.__fsMustPlay = new WeakMap(); } catch(_) {}
            try {
              const vid = window.__fsActiveVideo;
              if (vid) resetVideoPlayer(vid);
              window.__fsActiveVideo = null;
            } catch(_) {}
            // For mobile stacked, also center the slide containing the video
            try { centerStackForVideo(v); } catch(_) {}
            // Force pause slideshow video, then resume background score (iOS)
            try { v.pause(); } catch(_) {}
            // iOS Safari sometimes fails to render paused frame; force it
            try { ensurePausedFrameRenders(v); } catch(_) {}
            // Ensure the slide remains visible if layout collapsed
            try { ensureVisibleMediaAfterFS(v); } catch(_) {}
            try { audioPlayer.resumeFromSlideshow(); logSS('AUDIO_RESUME'); } catch(_) {}
            try { document.body.classList.remove('fs-playing'); if (window.__updateRotateMessage) window.__updateRotateMessage(); } catch(_) {}
          } catch(_) {}
        });
      } catch(_) {}
      slide.appendChild(vp);
    }
    wrapperEl.appendChild(slide);
  });

  // Wait for intrinsic sizes before initializing Swiper
  const waitForContentReady = () => new Promise((resolve) => {
    const imgs = Array.from(wrapperEl.querySelectorAll('img'));
    const vids = Array.from(wrapperEl.querySelectorAll('video'));
    let remaining = 0;
    const done = () => { if (remaining <= 0) resolve(); };
    const watchImage = (img) => {
      if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) return;
      remaining++;
      img.addEventListener('load', () => { remaining--; done(); }, { once: true });
      img.addEventListener('error', () => { remaining--; done(); }, { once: true });
    };
    const watchVideo = (vid) => {
      try { if (vid.readyState >= 1) return; } catch(_) {}
      remaining++;
      vid.addEventListener('loadedmetadata', () => { remaining--; done(); }, { once: true });
      vid.addEventListener('error', () => { remaining--; done(); }, { once: true });
    };
    imgs.forEach(watchImage);
    vids.forEach(watchVideo);
    setTimeout(() => resolve(), 800);
    done();
  });
  await waitForContentReady();

  // Compute uniform slide height and dynamic widths to maximize height
  // while keeping at least 10% of neighbors visible and preventing cropping
  // Position the fullscreen button to the actual video/poster bounds (object-fit: contain)
  const positionFullscreenButtonFor = (vp) => {
    try {
      if (!vp) return;
      const fs = vp.querySelector('.video-fs');
      if (!fs) return;
      const video = vp.querySelector('video');
      const poster = vp.querySelector('.video-poster');
      let targetEl = video;
      // If poster is visible, anchor to poster bounds
      if (poster && poster.style.display !== 'none') targetEl = poster;
      if (!targetEl) return;
      const rect = targetEl.getBoundingClientRect();
      const hostRect = vp.getBoundingClientRect();
      // Dynamic inset based on rendered bounds; slightly larger for safety
      const offsetTop = Math.max(18, Math.round(rect.height * 0.06));
      const offsetRight = Math.max(14, Math.round(rect.width * 0.02));
      // Clamp within the target element's visible bounds to avoid clipping
      // Lock to the inside of the top-right corner; if space is tight, clamp within bounds
      const leftCandidate = Math.round(rect.right - hostRect.left - fs.offsetWidth - offsetRight);
      const minLeftCandidate = Math.round(rect.left - hostRect.left + offsetRight);
      const leftPx = Math.max(minLeftCandidate, leftCandidate);

      const topCandidate = Math.round(rect.top - hostRect.top + offsetTop);
      const maxTopCandidate = Math.round(rect.bottom - hostRect.top - fs.offsetHeight - offsetTop);
      const topPx = Math.min(topCandidate, maxTopCandidate);
      fs.style.left = leftPx + 'px';
      fs.style.top = topPx + 'px';
      // Clear right to avoid conflicting positioning
      fs.style.right = '';
    } catch(_) {}
  };
  const positionAllFullscreenButtons = () => {
    try {
      const allVps = Array.from(host.querySelectorAll('.video-player-container'));
      allVps.forEach(vp => positionFullscreenButtonFor(vp));
    } catch(_) {}
  };
  const positionActiveVideoControls = () => {
    try {
      // Position for all video slides to avoid off-frame before activation
      positionAllFullscreenButtons();
    } catch(_) {}
  };

  const recomputeLayout = () => {
    const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const viewportRatio = viewportWidth / Math.max(viewportHeight, 1);
    // Enforce visible neighbor peek based on CSS variable
    let peekPadding = 0;
    try {
      const styles = window.getComputedStyle(modalEl);
      const rawPeek = styles.getPropertyValue('--peek-padding') || '0';
      peekPadding = Math.max(0, parseFloat(rawPeek));
    } catch(_) {}
    // Match Swiper gap used in config (spaceBetween)
    const SWIPER_GAP = 5;
    const section = modalEl.querySelector('.section-content');
    const slideshowHost = modalEl.querySelector('.slideshow');
    // Reduce max width by peek padding and both side gaps to ensure neighbors are visible
    const maxSlideWidthAllowed = Math.max(0, Math.floor(viewportWidth - (2 * peekPadding) - (2 * SWIPER_GAP)));
    // Use slideshow row height to size slides; section includes header and can overestimate
    const containerHeight = slideshowHost ? slideshowHost.clientHeight : (section ? section.clientHeight : window.innerHeight);
    const getAspect = (slideEl) => {
      const img = slideEl.querySelector('img.slide');
      const vid = slideEl.querySelector('video');
      if (img && img.naturalWidth && img.naturalHeight) return img.naturalWidth / img.naturalHeight;
      try { if (vid && vid.videoWidth && vid.videoHeight) return vid.videoWidth / vid.videoHeight; } catch(_) {}
      return 16/9; // sensible fallback
    };
    const slidesEls = Array.from(wrapperEl.children);
    const ratios = slidesEls.map(getAspect);
    const maxAR = ratios.reduce((m, r) => (r > m ? r : m), 1);
    // Fill available container height; width will be capped per slide to viewport width
    const targetHeight = Math.floor(containerHeight);
    modalEl.style.setProperty('--slide-height', `${targetHeight}px`);
    // Width per slide based on its aspect, capped to guarantee min neighbor visibility
    const isSafariDesktop = (() => {
      try {
        const ua = navigator.userAgent || '';
        const isSafari = /safari/i.test(ua) && !/chrome|crios|android/i.test(ua);
        const isDesktop = window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
        return isSafari && isDesktop;
      } catch(_) { return false; }
    })();
    const extraVideoOffsetPx = (() => {
      const r = viewportRatio;
      // Larger reduction on very wide landscapes, smaller on squarer viewports
      if (r >= 1.8) return 260;
      if (r >= 1.6) return 240;
      if (r >= 1.4) return 220;
      if (r >= 1.2) return 180;
      return 140;
    })();
    // Costume slideshow: reduce image widths on wide aspect ratios to keep height fully visible
    const extraImageOffsetPx = (() => {
      if (id !== 'best-costume-design') return 0;
      const r = viewportRatio;
      if (r >= 1.8) return 200;
      if (r >= 1.6) return 160;
      if (r >= 1.4) return 120;
      if (r >= 1.2) return 80;
      return 0;
    })();
    // Manage widths only when Swiper uses 'auto' slidesPerView; otherwise let Swiper handle fractional widths
    const sw = modalEl._swiper;
    const currentSPV = sw && sw.params ? sw.params.slidesPerView : 'auto';
    const useAutoWidths = (currentSPV === 'auto');
    if (useAutoWidths) {
      slidesEls.forEach((s, idx) => {
        const ar = ratios[idx] || maxAR || 1;
        let maxAllowed = maxSlideWidthAllowed;
        // Safari desktop: reduce width for video slides slightly
        if (isSafariDesktop && s.classList && s.classList.contains('has-video')) {
          maxAllowed = Math.max(0, Math.floor(maxSlideWidthAllowed - extraVideoOffsetPx));
        }
        // Costume slideshow: reduce width further on wide aspect ratios for image slides
        if (id === 'best-costume-design' && !(s.classList && s.classList.contains('has-video'))) {
          maxAllowed = Math.max(0, Math.floor(maxAllowed - extraImageOffsetPx));
        }
        const requiredWidth = Math.min(maxAllowed, Math.floor(targetHeight * ar));
        s.style.width = `${requiredWidth}px`;
      });
    } else {
      try { slidesEls.forEach(s => { s.style.width = ''; }); } catch(_) {}
    }
    // Sync video slide width to child poster/video rendered width (Safari)
    if (isSafariDesktop && useAutoWidths) {
      try {
        const videoSlides = Array.from(wrapperEl.querySelectorAll('.swiper-slide.has-video'));
        videoSlides.forEach((slide) => {
          const vp = slide.querySelector('.video-player-container');
          const poster = vp && vp.querySelector('.video-poster');
          const video = vp && vp.querySelector('video');
          const targetEl = (poster && poster.style.display !== 'none') ? poster : video;
          if (!targetEl) return;
          const prevWidth = slide.style.width;
          const childWidth = Math.floor(targetEl.getBoundingClientRect().width);
          if (childWidth > 0) {
            // Clear before measuring and applying the refined width
            try { slide.style.width = ''; } catch(_) {}
            const currentMax = Math.floor(Math.min(maxSlideWidthAllowed, slide.getBoundingClientRect().width));
            const nextWidth = Math.min(currentMax, childWidth);
            slide.style.width = `${nextWidth}px`;
          } else {
            // If the child hasn't painted yet (often right after fullscreen exit),
            // keep the previously computed width to avoid collapsing the slide.
            try { slide.style.width = prevWidth; } catch(_) {}
          }
        });
      } catch(_) {}
    }
    // Sync SVG image slide width to actual rendered child width (prevents oversized SVG after fullscreen)
    if (useAutoWidths) {
      try {
        const svgPaddedSlides = Array.from(wrapperEl.querySelectorAll('.swiper-slide .svg-padded'));
        svgPaddedSlides.forEach((pad) => {
          const slide = pad.closest('.swiper-slide');
          if (!slide) return;
          try { slide.style.width = ''; } catch(_) {}
          const img = pad.querySelector('img.slide');
          if (!img) return;
          const childWidth = Math.floor(img.getBoundingClientRect().width);
          if (childWidth > 0) {
            const nextWidth = Math.min(maxSlideWidthAllowed, childWidth);
            slide.style.width = `${nextWidth}px`;
          }
        });
      } catch(_) {}
    }
    // After sizing, make sure video fullscreen buttons align to the video bounds
    try { positionActiveVideoControls(); } catch(_) {}
  };
  recomputeLayout();

  // Redraw/recalculate on window resize (desktop): keep slide height in sync with viewport
  let _resizeRAF = 0;
  let _resizeClearTimer = 0;
  const onWindowResize = () => {
    // Only act when this slideshow is open
    if (!modalEl.classList || !modalEl.classList.contains('open')) return;
    // Mark as resizing to suppress transform/transition on the nav dropdown
    try { modalEl.classList.add('resizing'); } catch(_) {}
    if (_resizeRAF) cancelAnimationFrame(_resizeRAF);
    _resizeRAF = requestAnimationFrame(() => {
      try {
        recomputeLayout();
        const sw = modalEl._swiper;
        if (sw) {
          const idx = (typeof sw.activeIndex === 'number') ? sw.activeIndex : 0;
          sw.updateSlides();
          sw.update();
          sw.slideTo(idx, 0, false);
          try { positionActiveVideoControls(); } catch(_) {}
        }
      } catch(_) {}
    });
    // Remove resizing flag shortly after resize settles
    if (_resizeClearTimer) { try { clearTimeout(_resizeClearTimer); } catch(_) {} }
    _resizeClearTimer = setTimeout(() => { try { modalEl.classList.remove('resizing'); } catch(_) {} }, 400);
  };
  // Store listener to bind only when modal opens; remove on close to avoid stacking
  try { modalEl._onWindowResize = onWindowResize; } catch(_) {}

  // Controls
  const prev = document.createElement('div'); prev.className = 'slideshow-control prev'; prev.setAttribute('role','button'); prev.innerHTML = '<img src="/img/slideshow-prev.svg" alt="">';
  const next = document.createElement('div'); next.className = 'slideshow-control next'; next.setAttribute('role','button'); next.innerHTML = '<img src="/img/slideshow-next.svg" alt="">';
  host.appendChild(prev); host.appendChild(next);

  const slideCount = Array.isArray(sources) ? sources.length : 0;
  const smallLoop = slideCount > 0 && slideCount <= 4;
  // Try 'auto' for Best Costume Design regardless of slide count
  const slidesPerViewValue = (id === 'best-costume-design') ? 'auto' : (smallLoop ? 1.12 : 'auto');
  const swiper = new Swiper(swiperEl, {
    modules: [Navigation],
    slidesPerView: slidesPerViewValue,
    centeredSlides: true,
    centeredSlidesBounds: true,
    centerInsufficientSlides: true,
    spaceBetween: 5,
    speed: 800,
    loop: true,
    roundLengths: true,
    initialSlide: 0,
    preventClicks: true,
    preventClicksPropagation: true,
    observer: true,
    observeParents: true,
    updateOnWindowResize: true,
    watchSlidesProgress: true,
    on: {
      init: () => {
        try {
          recomputeLayout();
          swiper.updateSlides();
          swiper.update();
          // Center on the first real slide in loop mode
          try {
            if (typeof swiper.slideToLoop === 'function') swiper.slideToLoop(0, 0, false);
            else swiper.slideTo(0, 0, false);
          } catch(_) {}
          try { positionActiveVideoControls(); } catch(_) {}
        } catch(_) {}
      }
    },
    breakpoints: {
      0: { slidesPerView: slidesPerViewValue, centeredSlides: true, spaceBetween: 5 },
      768: { slidesPerView: slidesPerViewValue, centeredSlides: true, spaceBetween: 5 }
    }
  });
  // Recompute right after init to size slides and center first real slide
  try {
    recomputeLayout();
    swiper.updateSlides();
    swiper.update();
    try {
      if (typeof swiper.slideToLoop === 'function') swiper.slideToLoop(0, 0, false);
      else swiper.slideTo(0, 0, false);
    } catch(_) {}
    try { positionActiveVideoControls(); } catch(_) {}
  } catch(_) {}
  try { modalEl._activeSlideIndex = 0; } catch(_) {}
  // Track active slide index for fullscreen restore
  try { modalEl._activeSlideIndex = (typeof swiper.realIndex === 'number') ? swiper.realIndex : (typeof swiper.activeIndex === 'number' ? swiper.activeIndex : 0); } catch(_) {}

  // Custom prev: step exactly one real slide using realIndex + slideToLoop
  let prevLocked = false;
  let nextLocked = false;
  let navGuardUntil = 0;
  const unlockAfter = (ms = 900) => {
    try {
      setTimeout(() => { prevLocked = false; nextLocked = false; navGuardUntil = 0; }, ms);
    } catch(_) {}
  };
  const getRealCount = () => {
    try { return Array.from(swiper.slides || []).filter(s => !s.classList.contains('swiper-slide-duplicate')).length; } catch(_) { return 0; }
  };
  const findDirectionalTargetSlideIndex = (targetRealIdx, forward = true) => {
    try {
      const slidesArr = Array.from(swiper.slides || []);
      const matches = slidesArr.map((s, i) => ({ i, real: parseInt(s.getAttribute('data-swiper-slide-index') || '-1', 10) }))
        .filter(x => x.real === targetRealIdx);
      if (!matches.length) return -1;
      const active = typeof swiper.activeIndex === 'number' ? swiper.activeIndex : 0;
      const n = slidesArr.length;
      // Choose nearest in the intended direction
      const scored = matches.map(m => {
        const delta = (m.i - active + n) % n; // forward distance
        const backDelta = (active - m.i + n) % n; // backward distance
        return { i: m.i, fwd: delta, back: backDelta };
      });
      if (forward) {
        scored.sort((a, b) => a.fwd - b.fwd);
        return scored[0].i;
      } else {
        scored.sort((a, b) => a.back - b.back);
        return scored[0].i;
      }
    } catch(_) { return -1; }
  };
  const goPrev = (e) => {
    try { e.preventDefault(); e.stopPropagation(); } catch(_) {}
    const now = Date.now();
    if (!swiper || prevLocked || now < navGuardUntil || swiper.animating) return;
    prevLocked = true;
    navGuardUntil = now + 450;
    try {
      // Prefer stepping by realIndex to avoid clone/width drift
      const realCount = getRealCount();
      const curReal = typeof swiper.realIndex === 'number' ? swiper.realIndex : 0;
      const targetReal = realCount > 0 ? ((curReal - 1 + realCount) % realCount) : 0;
      if (typeof swiper.slideToLoop === 'function') {
        swiper.slideToLoop(targetReal, 800, false);
      } else {
        const dupIdx = findDirectionalTargetSlideIndex(targetReal, false);
        if (dupIdx >= 0 && typeof swiper.slideTo === 'function') {
          swiper.slideTo(dupIdx, 800, false);
        } else if (typeof swiper.slidePrev === 'function') {
          swiper.slidePrev(800, false);
        }
      }
      unlockAfter(900);
    } catch(_) { prevLocked = false; }
  };
  // Custom next: step exactly one real slide using realIndex + slideToLoop
  const goNext = (e) => {
    try { e.preventDefault(); e.stopPropagation(); } catch(_) {}
    const now = Date.now();
    if (!swiper || nextLocked || now < navGuardUntil || swiper.animating) return;
    nextLocked = true;
    navGuardUntil = now + 450;
    try {
      // Prefer stepping by realIndex to avoid clone/width drift
      const realCount = getRealCount();
      const curReal = typeof swiper.realIndex === 'number' ? swiper.realIndex : 0;
      const targetReal = realCount > 0 ? ((curReal + 1) % realCount) : 0;
      if (typeof swiper.slideToLoop === 'function') {
        swiper.slideToLoop(targetReal, 800, false);
      } else {
        const dupIdx = findDirectionalTargetSlideIndex(targetReal, true);
        if (dupIdx >= 0 && typeof swiper.slideTo === 'function') {
          swiper.slideTo(dupIdx, 800, false);
        } else if (typeof swiper.slideNext === 'function') {
          swiper.slideNext(800, false);
        }
      }
      unlockAfter(900);
    } catch(_) { nextLocked = false; }
  };
  // Use pointerdown to avoid duplicate click firing; still support keyboard
  prev.addEventListener('pointerdown', goPrev);
  prev.addEventListener('keydown', (e) => { if (['Enter',' '].includes(e.key)) goPrev(e); });
  next.addEventListener('pointerdown', goNext);
  next.addEventListener('keydown', (e) => { if (['Enter',' '].includes(e.key)) goNext(e); });
  swiper.on('slideChangeTransitionEnd', () => { prevLocked = false; nextLocked = false; navGuardUntil = 0; });
  swiper.on('transitionEnd', () => { prevLocked = false; nextLocked = false; navGuardUntil = 0; });

  // Recompute layout on Swiper resize events to keep height maximized
  swiper.on('resize', () => {
    try {
      recomputeLayout();
      const idx = (typeof swiper.activeIndex === 'number') ? swiper.activeIndex : 0;
      swiper.updateSlides();
      swiper.update();
      swiper.slideTo(idx, 0, false);
      try { positionActiveVideoControls(); } catch(_) {}
    } catch(_) {}
  });

  swiper.on('slideChange', () => {
    try { modalEl._activeSlideIndex = (typeof swiper.realIndex === 'number') ? swiper.realIndex : (typeof swiper.activeIndex === 'number' ? swiper.activeIndex : modalEl._activeSlideIndex || 0); } catch(_) {}
    const activeSlide = host.querySelector('.swiper-slide.swiper-slide-active');
    const slides = Array.from(host.querySelectorAll('.swiper-slide'));
    let pausedPlayingVideo = false;
    slides.forEach((s) => {
      const overlay = s.querySelector('.video-overlay');
      const v = s.querySelector('video');
      if (v) {
        if (s !== activeSlide) {
          const wasPlaying = !v.paused && !v.ended;
          try { v.pause(); } catch(_) {}
          if (wasPlaying) pausedPlayingVideo = true;
        }
        const poster = s.querySelector('.video-poster');
        if (poster) {
          try {
            const hidePoster = !!v._hasPlayedOnce || !v.paused;
            poster.style.display = hidePoster ? 'none' : '';
          } catch(_) {}
        }
      }
      if (overlay) {
        if (s === activeSlide && v && v.paused) {
          overlay.classList.add('show-play');
          overlay.classList.remove('show-pause');
        } else {
          overlay.classList.remove('show-play');
          overlay.classList.remove('show-pause');
        }
      }
    });
    // If we paused a playing slideshow video due to slide change, resume score audio
    try {
      const anyPlaying = Array.from(host.querySelectorAll('.swiper-slide video')).some(v => {
        try { return !v.paused && !v.ended; } catch(_) { return false; }
      });
      if (pausedPlayingVideo && !anyPlaying) {
        try { audioPlayer.resumeFromSlideshow(); } catch(_) {}
      }
    } catch(_) {}
  });

  // Ensure active slide remains centered after transitions
  const recenterActive = () => {
    try {
      const idx = (typeof swiper.activeIndex === 'number') ? swiper.activeIndex : 0;
      swiper.updateSlides();
      swiper.update();
      swiper.slideTo(idx, 0, false);
      try { positionActiveVideoControls(); } catch(_) {}
    } catch(_) {}
  };
  swiper.on('slideChangeTransitionEnd', recenterActive);
  swiper.on('transitionEnd', recenterActive);

  modalEl._swiper = swiper;
  modalEl._recomputeLayout = recomputeLayout;
  modalEl.dataset.built = '1';
  try { modalEl._mobileStack = false; } catch(_) {}
  try {
    if (section) {
      section.style.visibility = 'visible';
      section.removeAttribute('aria-busy');
    }
    modalEl.dataset.ready = '1';
  } catch(_) {}

  // If open, bind resize listener now that swiper exists
  try {
    if (modalEl._onWindowResize && modalEl.classList && modalEl.classList.contains('open') && !modalEl._resizeBound) {
      window.addEventListener('resize', modalEl._onWindowResize, { passive: true });
      modalEl._resizeBound = true;
    }
  } catch(_) {}

  // Event delegation for video interactions (covers loop clones)
  const pauseOtherVideos = (current) => {
    try {
      const all = Array.from(host.querySelectorAll('video'));
      all.forEach(v => { if (v !== current) { try { v.pause(); } catch(_) {} } });
    } catch(_) {}
  };
  // Compact slideshow debug logger
  const logSS = (tag, data) => {
    try {
      if (!window.__DEBUG_SLIDESHOW) return;
      const parts = [];
      if (data && typeof data === 'object') {
        for (const k in data) {
          const v = data[k];
          if (v === undefined) continue;
          parts.push(`${k}=${v}`);
        }
      }
      const msg = parts.length ? ` ${parts.join(' ')}` : '';
      console.debug(`[SS] ${tag}${msg}`);
    } catch(_) {}
  };
  const togglePlay = (video) => {
    try {
      if (!video) return;
      if (video.paused) {
        pauseOtherVideos(video);
        video.play().catch(()=>{});
      } else {
        video.pause();
      }
    } catch(_) {}
  };
  const syncOverlayAndPosterFor = (video) => {
    try {
      if (!video) return;
      const vp = video.closest('.video-player-container');
      const poster = vp && vp.querySelector('.video-poster');
      const overlay = vp && vp.querySelector('.video-overlay');
      if (poster) {
        const hidePoster = !!video._hasPlayedOnce || !video.paused;
        poster.style.display = hidePoster ? 'none' : '';
      }
      if (overlay) {
        if (video.paused) overlay.classList.add('show-play');
        else overlay.classList.remove('show-play', 'show-pause');
      }
    } catch(_) {}
  };
  const resetVideoPlayer = (video) => {
    try {
      if (!video) return;
      try { video.pause(); } catch(_) {}
      const vp = video.closest('.video-player-container');
      const poster = vp && vp.querySelector('.video-poster');
      if (poster) {
        try {
          const hidePoster = !!video._hasPlayedOnce;
          poster.style.display = hidePoster ? 'none' : '';
        } catch(_) {}
      }
      const overlay = vp && vp.querySelector('.video-overlay');
      if (overlay) { overlay.classList.add('show-play'); overlay.classList.remove('show-pause'); }
    } catch(_) {}
  };
  const centerStackForVideo = (video) => {
    try {
      if (!video) return;
      const modal = video.closest('.nav-dropdown.slideshow');
      if (!modal) return;
      const host = modal.querySelector('.slideshow');
      if (!host) return;
      // Only for mobile stacked layout (no Swiper instance)
      if (modal._swiper) return;
      const slideWrap = video.closest('.stack-slide') || video.closest('.slide') || video.parentElement;
      if (!slideWrap) return;
      const hostRect = host.getBoundingClientRect();
      const slideRect = slideWrap.getBoundingClientRect();
      const current = host.scrollTop;
      const deltaTop = slideRect.top - hostRect.top; // position of slide inside host viewport
      const desired = current + deltaTop - Math.max(0, (host.clientHeight - slideWrap.clientHeight) / 2);
      const maxScroll = host.scrollHeight - host.clientHeight;
      const target = Math.max(0, Math.min(maxScroll, desired));
      host.scrollTo({ top: Math.round(target), behavior: 'smooth' });
    } catch(_) {}
  };
  // Fullscreen playback guard and retry helpers
  const setMustPlayGuard = (video, ms = 2500) => {
    try {
      if (!window.__fsMustPlay) window.__fsMustPlay = new WeakMap();
      window.__fsMustPlay.set(video, Date.now() + ms);
      try {
        if (window.__DEBUG_SLIDESHOW) {
          console.debug('[SS] must-play guard set', { exp: window.__fsMustPlay.get(video), src: video && (video.currentSrc || video.src) });
        }
      } catch(_) {}
    } catch(_) {}
  };
  const mustPlayActive = (video) => {
    try {
      const exp = window.__fsMustPlay && window.__fsMustPlay.get(video);
      return !!(exp && Date.now() < exp);
    } catch(_) { return false; }
  };
  const isFullscreenVideo = (video) => {
    try {
      if (!video) return false;
      if (document.fullscreenElement === video) return true;
      // WebKit fullscreen element (older API)
      try { if (document.webkitFullscreenElement && document.webkitFullscreenElement === video) return true; } catch(_) {}
      // HTMLVideoElement native fullscreen flag (Safari)
      try { if (typeof video.webkitDisplayingFullscreen === 'boolean' && video.webkitDisplayingFullscreen) return true; } catch(_) {}
      // Fallback to our tracked active video during fullscreen
      try { if (window.__fsActiveVideo && window.__fsActiveVideo === video) return true; } catch(_) {}
      return false;
    } catch(_) { return false; }
  };
  const isSafariDesktop = () => {
    try {
      const ua = navigator.userAgent || '';
      const isSafariBrand = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/CriOS\//.test(ua) && !/Edg\//.test(ua);
      const isiOS = /iP(hone|ad|od)/i.test(ua) || ((navigator.platform||'').includes('Mac') && navigator.maxTouchPoints > 1);
      const isMobileToken = /Mobile\//.test(ua);
      // Treat iPadOS Safari as desktop-like even if it includes Mobile token
      const isDesktopLike = !isMobileToken || isiOS;
      return isSafariBrand && isDesktopLike;
    } catch(_) { return false; }
  };
  const forcePlay = (video) => {
    try { video.play().catch(()=>{}); } catch(_) {}
  };
  const scheduleForcePlay = (video) => {
    try {
      // Deduplicate scheduling: only one forcePlay loop per video
      if (!window.__fsForcePlayTimers) window.__fsForcePlayTimers = new WeakMap();
      if (window.__fsForcePlayTimers.get(video)) return;
      window.__fsForcePlayTimers.set(video, true);
      let attempts = 0;
      const marks = new Set([1,5,10,15,20,25]);
      const tick = () => {
        attempts++;
        // Only force play while guard is active; user pause should cancel guard
        if (!mustPlayActive(video)) { try { window.__fsForcePlayTimers.delete(video); } catch(_) {} return; }
        // Hard block: if set, do not attempt to resume
        try { if (window.__fsBlockPlay && window.__fsBlockPlay.has(video)) { try { window.__fsForcePlayTimers.delete(video); } catch(_) {} return; } } catch(_) {}
        // Respect explicit user pause intent
        try {
          if (window.__fsUserPaused && window.__fsUserPaused.has(video)) { try { window.__fsForcePlayTimers.delete(video); } catch(_) {} return; }
        } catch(_) {}
        // Compact tick log at marked attempts only
        if (marks.has(attempts)) {
          let p = 0, g = 0, u = 0, b = 0;
          try { p = video.paused ? 1 : 0; } catch(_) {}
          try { g = mustPlayActive(video) ? 1 : 0; } catch(_) {}
          try { u = (window.__fsUserPaused && window.__fsUserPaused.has(video)) ? 1 : 0; } catch(_) {}
          try { b = (window.__fsBlockPlay && window.__fsBlockPlay.has(video)) ? 1 : 0; } catch(_) {}
          logSS('TICK', { a: attempts, p, g, u, b });
        }
        if (video.paused) {
          forcePlay(video);
          if (attempts < 25) { setTimeout(tick, 120); }
          else { try { window.__fsForcePlayTimers.delete(video); } catch(_) {} }
        } else {
          // Playing: clear scheduled state
          try { window.__fsForcePlayTimers.delete(video); } catch(_) {}
        }
      };
      setTimeout(tick, 80);
    } catch(_) {}
  };
  // After FS exit, ensure something visible: paused frame or poster if layout collapses
  const ensureVisibleMediaAfterFS = (video) => {
    try {
      if (!video) return;
      const vp = video.closest('.video-player-container');
      if (!vp) return;
      const vRect = (() => { try { return video.getBoundingClientRect(); } catch(_) { return { width: 0, height: 0 }; } })();
      const vpRect = (() => { try { return vp.getBoundingClientRect(); } catch(_) { return { width: 0, height: 0 }; } })();
      const tooSmall = (Math.floor(vRect.width) < 40) || (Math.floor(vRect.height) < 40) || (Math.floor(vpRect.height) < 40);
      const poster = vp.querySelector('.video-poster');
      if (tooSmall && poster) {
        // Temporarily show poster to avoid blank slide; keep _hasPlayedOnce semantics
        try {
          const shouldHidePoster = !!video._hasPlayedOnce && !video.paused;
          poster.style.display = shouldHidePoster ? 'none' : '';
        } catch(_) {}
        // Recompute layout to restore slide sizing
        try {
          const modal = vp.closest('.nav-dropdown.slideshow');
          if (modal && typeof modal._recomputeLayout === 'function') modal._recomputeLayout();
          // Mobile stacked fallback: set a temporary min-height based on poster aspect
          if (modal && !modal._swiper) {
            const w = vp.clientWidth || 0;
            const pw = poster.naturalWidth || 0;
            const ph = poster.naturalHeight || 0;
            let expectedH = 0;
            if (w > 0 && pw > 0 && ph > 0) {
              expectedH = Math.max(140, Math.floor(w * (ph / pw)));
            } else {
              expectedH = 160; // sensible fallback to avoid collapse
            }
            try { vp.style.minHeight = expectedH + 'px'; } catch(_) {}
            // Clear min-height after a short settle window
            setTimeout(() => { try { vp.style.minHeight = ''; } catch(_) {} }, 500);
          }
        } catch(_) {}
        // Try another render nudge shortly after
        setTimeout(() => { try { ensurePausedFrameRenders(video); } catch(_) {} }, 64);
        logSS('IOS_VIS_FIX', { small: 1 });
      }
    } catch(_) {}
  };
  // iOS Safari: after exiting native fullscreen, force rendering a paused frame
  const ensurePausedFrameRenders = (video) => {
    try {
      if (!video) return;
      if (!video.paused) return;
      const ua = navigator.userAgent || '';
      const isiOS = /iP(hone|ad|od)/i.test(ua);
      const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !/CriOS\//.test(ua) && !/Edg\//.test(ua);
      if (!(isiOS && isSafari)) return;
      // If we have a decoded frame, try a quick play/pause or micro-seek to flush paint
      const hasFrame = (() => { try { return (video.readyState >= 2) && (video.currentTime || 0) >= 0; } catch(_) { return false; } })();
      if (hasFrame) {
        try {
          // Try a micro seek to force a repaint of the current frame
          const t = Number(video.currentTime || 0);
          const to = Math.max(0, t - 0.000001);
          video.currentTime = to;
          // Also try a quick play/pause sequence to flush the frame to screen
          const attempt = () => { try { video.play().then(() => { try { video.pause(); } catch(_) {} }).catch(() => {}); } catch(_) {} };
          // Stagger attempts a bit as Safari may need a tick
          attempt();
          setTimeout(attempt, 30);
          setTimeout(attempt, 90);
          logSS('IOS_FRAME_FIX', { tried: 1 });
        } catch(_) {}
      } else {
        // No frame available; show poster to avoid blank content
        try {
          const vp = video.closest('.video-player-container');
          const poster = vp && vp.querySelector('.video-poster');
          if (poster) poster.style.display = '';
          logSS('IOS_FRAME_POSTER_FALLBACK');
        } catch(_) {}
      }
      // Extra nudge: toggle a transform to force layer repaint
      try {
        const prev = video.style.transform || '';
        video.style.transform = 'translateZ(0)';
        setTimeout(() => { try { video.style.transform = prev; } catch(_) {} }, 60);
      } catch(_) {}
    } catch(_) {}
  };
  // Robust restore of Swiper to a specific real index after fullscreen (loop-aware)
  const restoreSwiperToIndex = (sw, modal, idx) => {
    try {
      if (!sw || idx == null) return;
      try { sw.allowSlidePrev = true; sw.allowSlideNext = true; } catch(_) {}
      // Helper: find nearest duplicate slide index for a given real index around a reference
      const findNearestDuplicateIndex = (swiperInstance, realIdx, nearIdx) => {
        try {
          const slidesArr = Array.from(swiperInstance.slides || []);
          const matches = slidesArr.map((s, i) => ({ i, real: parseInt(s.getAttribute('data-swiper-slide-index') || '-1', 10) }))
            .filter(x => x.real === realIdx);
          if (!matches.length) return -1;
          if (typeof nearIdx !== 'number' || !Number.isFinite(nearIdx)) return matches[0].i;
          const n = slidesArr.length || 1;
          let best = matches[0].i;
          let bestScore = Number.POSITIVE_INFINITY;
          matches.forEach(m => {
            const forward = (m.i - nearIdx + n) % n;
            const backward = (nearIdx - m.i + n) % n;
            const score = Math.min(forward, backward);
            if (score < bestScore) { bestScore = score; best = m.i; }
          });
          return best;
        } catch(_) { return -1; }
      };
      const resettleAfterFullscreen = (modalEl) => {
        try {
          const sww = modalEl && modalEl._swiper;
          if (!sww) return;
          // Clear any stale inline widths for all slides before remeasuring
          try { Array.from(modalEl.querySelectorAll('.swiper-slide')).forEach(s => { s.style.width = ''; }); } catch(_) {}
          try { if (typeof sww.updateSize === 'function') sww.updateSize(); } catch(_) {}
          try { if (typeof sww.updateSlides === 'function') sww.updateSlides(); } catch(_) {}
          try { if (typeof sww.update === 'function') sww.update(); } catch(_) {}
          const ai = (typeof sww.activeIndex === 'number') ? sww.activeIndex : 0;
          try { sww.slideTo(ai, 0, false); } catch(_) {}
          try { if (typeof modalEl._recomputeLayout === 'function') modalEl._recomputeLayout(); } catch(_) {}
          setTimeout(() => {
            try { if (typeof modalEl._recomputeLayout === 'function') modalEl._recomputeLayout(); } catch(_) {}
            try { positionActiveVideoControls(); } catch(_) {}
          }, 64);
        } catch(_) {}
      };
      const verifyAndFinalize = () => {
        try {
          if (typeof sw.updateSlides === 'function') sw.updateSlides();
          if (typeof sw.update === 'function') sw.update();
          // Force center on current active without animation
          const ai = (typeof sw.activeIndex === 'number') ? sw.activeIndex : 0;
          try { sw.slideTo(ai, 0, false); } catch(_) {}
        } catch(_) {}
        // Recompute layout multiple times to settle sizes after FS exit
        setTimeout(() => {
          try { if (typeof modal._recomputeLayout === 'function') modal._recomputeLayout(); } catch(_) {}
          setTimeout(() => { try { if (typeof modal._recomputeLayout === 'function') modal._recomputeLayout(); } catch(_) {} }, 64);
          try { modal._activeSlideIndex = idx; } catch(_) {}
        }, 32);
        try { positionActiveVideoControls(); } catch(_) {}
        resettleAfterFullscreen(modal);
      };
      // If we restored to a duplicate, verify realIndex matches the target; otherwise correct it
      const verifyRealIndex = () => {
        try {
          if (typeof sw.realIndex === 'number' && sw.realIndex !== idx) {
            if (typeof sw.slideToLoop === 'function') {
              try { sw.slideToLoop(idx, 0, false); } catch(_) {}
            } else {
              const dupIdx = findNearestDuplicateIndex(sw, idx, sw.activeIndex);
              if (dupIdx >= 0 && typeof sw.slideTo === 'function') {
                try { sw.slideTo(dupIdx, 0, false); } catch(_) {}
              }
            }
          }
        } catch(_) {}
      };
      // Prefer restoring to the exact duplicate index captured at FS entry
      let restoredViaDuplicate = false;
      try {
        if (modal && typeof modal._fsActiveIndexDup === 'number') {
          const dupIdx = findNearestDuplicateIndex(sw, idx, modal._fsActiveIndexDup);
          if (dupIdx >= 0 && typeof sw.slideTo === 'function') {
            try { sw.slideTo(dupIdx, 0, false); restoredViaDuplicate = true; } catch(_) {}
          }
        }
      } catch(_) {}
      try { if (modal) modal._fsActiveIndexDup = null; } catch(_) {}
      if (!restoredViaDuplicate && typeof sw.slideToLoop === 'function') {
        let attempts = 0;
        const maxAttempts = 6;
        const tryMove = () => {
          attempts++;
          try { sw.slideToLoop(idx, 0, false); } catch(_) {}
          setTimeout(() => {
            const ok = (typeof sw.realIndex === 'number') ? (sw.realIndex === idx) : true;
            if (!ok && attempts < maxAttempts) tryMove();
            else verifyAndFinalize();
          }, 32);
        };
        tryMove();
      } else {
        try { sw.slideTo(idx, 0, false); } catch(_) {}
        verifyAndFinalize();
      }
      // Double-check real index shortly after to correct any drift (with retries)
      let checks = 0;
      const recheck = () => {
        checks++;
        verifyRealIndex();
        if (checks < 5) setTimeout(recheck, 48);
      };
      setTimeout(recheck, 32);
    } catch(_) {}
  };
  const enterFullscreen = (video) => {
    try {
      if (!video) return;
      logSS('FS_ENTER', { src: (() => { try { return video.currentSrc || video.src; } catch(_) { return ''; } })() });
      pauseOtherVideos(video);
      try { audioPlayer.pauseForSlideshow(); } catch(_) {}
      // Mark as played and hide poster on FS entry so it stays hidden after exit
      try { markVideoInteracted(video); } catch(_) {}
      // Clear any previous user-pause flag so autoplay can proceed on new entry
      try { if (window.__fsUserPaused) window.__fsUserPaused.delete(video); } catch(_) {}
      // Clear any block play flags on new entry
      try { if (window.__fsBlockPlay) window.__fsBlockPlay.delete(video); } catch(_) {}
      // Bind direct pause/play listeners to the fullscreen video to respect native controls
      try {
        // Remove existing handlers if any
        const cleanupFSListeners = () => {
          try {
            if (video._fsPauseListener) { try { video.removeEventListener('pause', video._fsPauseListener, true); } catch(_) {} video._fsPauseListener = null; }
            if (video._fsPlayListener) { try { video.removeEventListener('play', video._fsPlayListener, true); } catch(_) {} video._fsPlayListener = null; }
            video._fsBound = null;
          } catch(_) {}
        };
        if (video._fsPauseListener) { try { video.removeEventListener('pause', video._fsPauseListener, true); } catch(_) {} }
        if (video._fsPlayListener) { try { video.removeEventListener('play', video._fsPlayListener, true); } catch(_) {} }
        if (video._fsBound) { cleanupFSListeners(); }
        // Define handlers
        video._fsPauseListener = () => {
          try {
            // Ignore early pause events during the FS guard window
            try {
              const guard = window.__fsPauseGuard && window.__fsPauseGuard.get(video);
              if (guard && Date.now() < guard) { logSS('FS_PAUSE_IGNORED'); return; }
            } catch(_) {}
            // Mark user intent to pause and block auto-resume loops
            if (!window.__fsUserPaused) window.__fsUserPaused = new WeakSet();
            window.__fsUserPaused.add(video);
            if (window.__fsMustPlay) window.__fsMustPlay.delete(video);
            if (window.__fsPauseGuard) window.__fsPauseGuard.delete(video);
            if (!window.__fsBlockPlay) window.__fsBlockPlay = new WeakSet();
            window.__fsBlockPlay.add(video);
            logSS('FS_PAUSE', { src: (() => { try { return video.currentSrc || video.src; } catch(_) { return ''; } })() });
            // Do NOT reassert pause repeatedly here; rely on flags to stop retries
          } catch(_) {}
        };
        video._fsPlayListener = () => {
          try {
            if (window.__fsUserPaused) window.__fsUserPaused.delete(video);
            if (window.__fsBlockPlay) window.__fsBlockPlay.delete(video);
            logSS('FS_PLAY', { src: (() => { try { return video.currentSrc || video.src; } catch(_) { return ''; } })() });
          } catch(_) {}
        };
        // Attach
        video.addEventListener('pause', video._fsPauseListener, true);
        video.addEventListener('play', video._fsPlayListener, true);
        // Also clean up direct listeners on Safari native fullscreen exit
        try { video.addEventListener('webkitendfullscreen', cleanupFSListeners, { once: true }); } catch(_) {}
        video._fsBound = true;
        try { if (window.__DEBUG_SLIDESHOW) console.debug('[SS] fs listeners bound'); } catch(_) {}
      } catch(_) {}
      // Suppress pause-side effects briefly during fullscreen entry
      try {
        if (!window.__fsPauseGuard) window.__fsPauseGuard = new WeakMap();
        window.__fsPauseGuard.set(video, Date.now() + 1500);
        // Capture current slide index for restore after fullscreen exit (desktop swiper)
        const vp = video.closest('.video-player-container');
        const modal = vp && vp.closest('.nav-dropdown.slideshow');
        if (modal) { window.__fsActiveModal = modal; }
      } catch(_) {}
      const ensurePlay = () => { try { video.play().catch(()=>{}); } catch(_) {} };
      // Proactively start playback before requesting fullscreen (helps iOS Safari)
      ensurePlay();
      logSS('ENSURE_PLAY');
      // Hide poster/overlay immediately to reduce flash
      try {
        const vp = video.closest('.video-player-container');
        const poster = vp && vp.querySelector('.video-poster');
        const overlay = vp && vp.querySelector('.video-overlay');
        if (poster) poster.style.display = 'none';
        if (overlay) overlay.classList.remove('show-play', 'show-pause');
      } catch(_) {}
      // Try to start playback after entering fullscreen
      if (typeof video.webkitEnterFullscreen === 'function') {
        try { video.addEventListener('webkitbeginfullscreen', ensurePlay, { once: true }); } catch(_) {}
        logSS('FS_API', { api: 'webkit' });
        try { video.webkitEnterFullscreen(); } catch(_) {}
        // Also attempt immediate play after calling fullscreen
        ensurePlay();
        logSS('ENSURE_PLAY');
      } else if (typeof video.requestFullscreen === 'function') {
        try {
          logSS('FS_API', { api: 'std' });
          const p = video.requestFullscreen();
          if (p && typeof p.then === 'function') p.then(ensurePlay).catch(() => ensurePlay());
          else ensurePlay();
          logSS('ENSURE_PLAY');
        } catch(_) { ensurePlay(); }
      } else {
        ensurePlay();
      }
      // Enforce playback while entering fullscreen
      // iOS/Safari native path: schedule immediately
      // For standard Fullscreen API on desktop, rely on document fullscreenchange
      if (typeof video.webkitEnterFullscreen === 'function') {
        setMustPlayGuard(video);
        logSS('GUARD_SET', { ms: 2500 });
        scheduleForcePlay(video);
        logSS('RETRY_SCHEDULED');
      }
      try { window.__fsActiveVideo = video; } catch(_) {}
    } catch(_) {}
  };
  host.addEventListener('click', (e) => {
    const fsButton = e.target && typeof e.target.closest === 'function' ? e.target.closest('.video-fs') : null;
    if (fsButton) {
      const vp = fsButton.closest('.video-player-container');
      const slide = fsButton.closest('.swiper-slide');
      if (!slide || !slide.classList.contains('swiper-slide-active')) {
        // Only allow fullscreen/play from the active slide
        return;
      }
      // Record the exact real slide index to restore on fullscreen exit
      try {
        const modal = fsButton.closest('.nav-dropdown.slideshow');
        // Prefer the slide's data-swiper-slide-index (real index), fall back to swiper.realIndex
        let idx = null;
        const raw = slide && slide.getAttribute && slide.getAttribute('data-swiper-slide-index');
        if (raw != null) idx = parseInt(raw, 10);
        if (!Number.isFinite(idx)) {
          const sw = modal && modal._swiper;
          if (sw && typeof sw.realIndex === 'number') idx = sw.realIndex;
        }
        if (modal && Number.isFinite(idx)) modal._fsTargetIndex = idx;
        // Also capture the duplicate-based active index to restore the exact clone
        try {
          const sw = modal && modal._swiper;
          if (sw && typeof sw.activeIndex === 'number') modal._fsActiveIndexDup = sw.activeIndex;
        } catch(_) {}
        try {
          if (window.__DEBUG_SLIDESHOW) {
            const sw = modal && modal._swiper;
            console.debug('[SS] FS open (click)', {
              modal: modal && modal.getAttribute && modal.getAttribute('data-id'),
              raw,
              storedIdx: idx,
              swiperReal: sw && sw.realIndex,
              swiperActive: sw && sw.activeIndex
            });
          }
        } catch(_) {}
        if (modal) window.__fsActiveModal = modal;
      } catch(_) {}
      const video = vp && vp.querySelector('video');
      logSS('FS_BTN', { src: (() => { try { return video.currentSrc || video.src; } catch(_) { return ''; } })() });
      // If user-pause/block flags are set for the fullscreen video, always respect pause
      try {
        const isBlocked = !!(
          (window.__fsUserPaused && window.__fsUserPaused.has(video)) ||
          (window.__fsBlockPlay && window.__fsBlockPlay.has(video))
        );
        if (isBlocked && isFullscreenVideo(video)) {
          if (window.__fsMustPlay) window.__fsMustPlay.delete(video);
          if (window.__fsPauseGuard) window.__fsPauseGuard.delete(video);
          logSS('BLOCKED_PAUSE', { src: (() => { try { return video.currentSrc || video.src; } catch(_) { return ''; } })() });
          return; // do not enter fullscreen
        }
      } catch(_) {}
      enterFullscreen(video);
      return;
    }
    const vp = e.target && typeof e.target.closest === 'function' ? e.target.closest('.video-player-container') : null;
    if (vp) {
      const slide = vp.closest('.swiper-slide');
      if (!slide || !slide.classList.contains('swiper-slide-active')) {
        // Only allow play/pause toggles on the active slide
        return;
      }
      const video = vp.querySelector('video');
      togglePlay(video);
    }
  });
  // Show/hide poster based on play/pause/ended using capture to cover clones
  host.addEventListener('play', (e) => {
    const video = e.target;
    const vp = video && typeof video.closest === 'function' ? video.closest('.video-player-container') : null;
    const poster = vp && vp.querySelector('.video-poster');
    try { markVideoInteracted(video); } catch(_) {}
    if (poster) { try { poster.style.display = 'none'; } catch(_) {} }
    const overlay = vp && vp.querySelector('.video-overlay');
    if (overlay) { overlay.classList.remove('show-play', 'show-pause'); }
    // Clear any user-pause flags on resume
    try { if (window.__fsUserPaused) window.__fsUserPaused.delete(video); } catch(_) {}
    try { if (window.__fsBlockPlay) window.__fsBlockPlay.delete(video); } catch(_) {}
    logSS('PLAY', { src: (() => { try { return video.currentSrc || video.src; } catch(_) { return ''; } })(), p: (() => { try { return video.paused ? 1 : 0; } catch(_) { return 0; } })() });
    // Pause background score audio while slideshow video plays
    try { audioPlayer.pauseForSlideshow(); } catch(_) {}
  }, true);
  host.addEventListener('pause', (e) => {
    const video = e.target;
    // If pause occurs during or immediately after entering fullscreen, or while must-play guard is active, force resume
    try {
      // On Safari desktop, any pause in fullscreen should be respected
      try {
        if (isSafariDesktop() && isFullscreenVideo(video)) {
          if (window.__fsMustPlay) window.__fsMustPlay.delete(video);
          if (window.__fsPauseGuard) window.__fsPauseGuard.delete(video);
          // Mark user pause intent so retry loop won't resume
          try { if (!window.__fsUserPaused) window.__fsUserPaused = new WeakSet(); window.__fsUserPaused.add(video); } catch(_) {}
          try { if (!window.__fsBlockPlay) window.__fsBlockPlay = new WeakSet(); window.__fsBlockPlay.add(video); } catch(_) {}
          throw new Error('__skip_autoresume');
        }
      } catch(_) {}
      // Respect explicit user pause on fullscreen video
      const manual = window.__fsManualPause && window.__fsManualPause.has(video);
      if (manual) {
        try { window.__fsManualPause.delete(video); } catch(_) {}
        // Clear guards so pause sticks
        try { if (window.__fsMustPlay) window.__fsMustPlay.delete(video); } catch(_) {}
        try { if (window.__fsPauseGuard) window.__fsPauseGuard.delete(video); } catch(_) {}
        // Mark user pause intent
        try { if (!window.__fsUserPaused) window.__fsUserPaused = new WeakSet(); window.__fsUserPaused.add(video); } catch(_) {}
        // proceed to overlay/poster updates below without auto-resume
        throw new Error('__skip_autoresume');
      }
      const guard = window.__fsPauseGuard && window.__fsPauseGuard.get(video);
      const now = Date.now();
      const inGuardWindow = guard && now < guard;
      // Allow pausing while in fullscreen once initial guard period passes
      const entryAt = window.__fsEntryAt && window.__fsEntryAt.get(video);
      const withinEntry = !!(entryAt && (now - entryAt) < 1500);
      if (inGuardWindow || withinEntry) {
        // During entry or guard, auto-resume playback
        try { video.play().catch(()=>{}); } catch(_) {}
        return; // don't resume bg audio or flip overlays yet
      }
      // Outside entry/guard windows: clear any lingering must-play so pause persists
            logSS('PAUSE', { src: (() => { try { return video.currentSrc || video.src; } catch(_) { return ''; } })(), reason: 'safari-fs' });
      try { if (window.__fsMustPlay) window.__fsMustPlay.delete(video); } catch(_) {}
      try { if (window.__fsPauseGuard) window.__fsPauseGuard.delete(video); } catch(_) {}
      // Mark user pause intent so retry loop won't resume
      try { if (!window.__fsUserPaused) window.__fsUserPaused = new WeakSet(); window.__fsUserPaused.add(video); } catch(_) {}
      try { if (!window.__fsBlockPlay) window.__fsBlockPlay = new WeakSet(); window.__fsBlockPlay.add(video); } catch(_) {}
    } catch(_) {}
    const vp = video && typeof video.closest === 'function' ? video.closest('.video-player-container') : null;
    const slide = vp && vp.closest('.swiper-slide');
    const modal = vp && vp.closest('.nav-dropdown.slideshow');
    const sw = modal && modal._swiper;
    const poster = vp && vp.querySelector('.video-poster');
    if (poster) { try { poster.style.display = video && video._hasPlayedOnce ? 'none' : ''; } catch(_) {} }
    const overlay = vp && vp.querySelector('.video-overlay');
    if (overlay) {
      const isDesktopActive = !!(sw && slide && slide.classList.contains('swiper-slide-active'));
      const isStacked = !sw;
      if (isDesktopActive || isStacked) overlay.classList.add('show-play');
      else overlay.classList.remove('show-play');
      overlay.classList.remove('show-pause');
    }
    // Resume background score audio when user pauses the slideshow video,
    // with iOS user-gesture fallback to satisfy autoplay policy
    try {
      const anyPlaying = Array.from(document.querySelectorAll('.video-player-container video')).some(v => {
        try { return !v.paused && !v.ended; } catch(_) { return false; }
      });
      if (!anyPlaying) {
        try { resumeScoreAfterFullscreenIfIdle(); } catch(_) {}
      }
    } catch(_) {}
  }, true);
  host.addEventListener('ended', (e) => {
    const video = e.target;
    const vp = video && typeof video.closest === 'function' ? video.closest('.video-player-container') : null;
    const slide = vp && vp.closest('.swiper-slide');
    const modal = vp && vp.closest('.nav-dropdown.slideshow');
    const sw = modal && modal._swiper;
    const poster = vp && vp.querySelector('.video-poster');
    if (poster) { try { poster.style.display = video && video._hasPlayedOnce ? 'none' : ''; } catch(_) {} }
    const overlay = vp && vp.querySelector('.video-overlay');
    if (overlay) {
      const isDesktopActive = !!(sw && slide && slide.classList.contains('swiper-slide-active'));
      const isStacked = !sw;
      if (isDesktopActive || isStacked) overlay.classList.add('show-play');
      else overlay.classList.remove('show-play');
      overlay.classList.remove('show-pause');
    }
    // Resume background score audio when slideshow video ends,
    // with iOS user-gesture fallback to satisfy autoplay policy
    try { resumeScoreAfterFullscreenIfIdle(); } catch(_) {}
  }, true);

  // Sync UI on fullscreen changes (non-iOS)
  try {
    if (!window.__slideshowFSBound) {
      document.addEventListener('fullscreenchange', () => {
        try {
          const fsEl = document.fullscreenElement;
          if (fsEl && fsEl.tagName && fsEl.tagName.toLowerCase() === 'video') {
            try { document.body.classList.add('fs-playing'); if (window.__updateRotateMessage) window.__updateRotateMessage(); } catch(_) {}
            syncOverlayAndPosterFor(fsEl);
            // Clear guard sooner once fullscreen established
            try { if (window.__fsPauseGuard) window.__fsPauseGuard.delete(fsEl); } catch(_) {}
            // Enforce playback during fullscreen
            try { setMustPlayGuard(fsEl, 3000); scheduleForcePlay(fsEl); } catch(_) {}
            try { window.__fsActiveVideo = fsEl; } catch(_) {}
            // Record fullscreen entry timestamp for this video
            try { if (!window.__fsEntryAt) window.__fsEntryAt = new WeakMap(); window.__fsEntryAt.set(fsEl, Date.now()); } catch(_) {}
            logSS('FS_CHANGE_ENTER', { src: (() => { try { return fsEl.currentSrc || fsEl.src; } catch(_) { return ''; } })() });
            // Capture modal, real index and duplicate index at entry (desktop Safari Fullscreen API)
            try {
              const slideEl = fsEl.closest('.swiper-slide');
              const modal = slideEl && slideEl.closest && slideEl.closest('.nav-dropdown.slideshow');
              if (modal) window.__fsActiveModal = modal;
              const sw = modal && modal._swiper;
              if (slideEl) {
                const rawReal = slideEl.getAttribute && slideEl.getAttribute('data-swiper-slide-index');
                const parsed = rawReal != null ? parseInt(rawReal, 10) : NaN;
                if (Number.isFinite(parsed)) modal._fsTargetIndex = parsed;
              }
              if (sw && typeof sw.activeIndex === 'number') modal._fsActiveIndexDup = sw.activeIndex;
            } catch(_) {}
          } else {
            try { document.body.classList.remove('fs-playing'); if (window.__updateRotateMessage) window.__updateRotateMessage(); } catch(_) {}
            // Sync all videos after exiting fullscreen
            Array.from(document.querySelectorAll('.video-player-container video')).forEach(v => syncOverlayAndPosterFor(v));
            // Clear all guards on exit
            try { if (window.__fsPauseGuard) window.__fsPauseGuard = new WeakMap(); } catch(_) {}
            try { if (window.__fsMustPlay) window.__fsMustPlay = new WeakMap(); } catch(_) {}
            // Remove direct FS video handlers
            try {
              const vid = window.__fsActiveVideo;
              if (vid) {
                if (vid._fsPauseListener) { try { vid.removeEventListener('pause', vid._fsPauseListener, true); } catch(_) {} vid._fsPauseListener = null; }
                if (vid._fsPlayListener) { try { vid.removeEventListener('play', vid._fsPlayListener, true); } catch(_) {} vid._fsPlayListener = null; }
                try { vid._fsBound = null; } catch(_) {}
              }
            } catch(_) {}
            logSS('FS_CHANGE_EXIT');
            // Restore slide index if needed using stored target (or active)
            const modal = window.__fsActiveModal;
            const sw = modal && modal._swiper;
            let idx = null;
            // Derive the exact duplicate index from the active fullscreen video
            try {
              const vid = window.__fsActiveVideo;
              if (vid && sw) {
                const slideEl = vid.closest('.swiper-slide');
                const slidesArr = Array.from(sw.slides || []);
                const dupIdx = slidesArr.indexOf(slideEl);
                if (dupIdx >= 0) modal._fsActiveIndexDup = dupIdx;
                const rawReal = slideEl && slideEl.getAttribute && slideEl.getAttribute('data-swiper-slide-index');
                const parsed = rawReal != null ? parseInt(rawReal, 10) : NaN;
                if (Number.isFinite(parsed)) idx = parsed;
              }
            } catch(_) {}
            if (modal) {
              if (typeof modal._fsTargetIndex === 'number') idx = modal._fsTargetIndex;
              else if (typeof modal._activeSlideIndex === 'number') idx = modal._activeSlideIndex;
            }
            if (sw && idx !== null) {
              restoreSwiperToIndex(sw, modal, idx);
              // Extra settle handled inside restore
            }
            try { if (modal) modal._fsTargetIndex = null; } catch(_) {}
            try { if (modal) modal._fsActiveIndexDup = null; } catch(_) {}
            try {
              const vid = window.__fsActiveVideo;
              if (vid) resetVideoPlayer(vid);
              window.__fsActiveVideo = null;
            } catch(_) {}
            // If no Swiper (mobile stacked), recentre the corresponding slide
            try {
              const vid = window.__fsActiveVideo;
              if (vid) centerStackForVideo(vid);
            } catch(_) {}
            window.__fsActiveModal = null;
            // Ensure visibility on iOS WebKit if the paused frame didn't render
            try {
              const vid = window.__fsActiveVideo;
              if (vid) ensureVisibleMediaAfterFS(vid);
            } catch(_) {}
            // Resume background score audio if no slideshow videos are playing
            try { logSS && logSS('FS_CHANGE_EXIT_RESUME_ATTEMPT'); } catch(_) {}
            try { resumeScoreAfterFullscreenIfIdle(); } catch(_) {}
          }
        } catch(_) {}
      }, true);
      window.__slideshowFSBound = true;
    }
  } catch(_) {}

  // Desktop fullscreen: toggle play/pause on click of the fullscreen video
          const isFS = isFullscreenVideo(t);
  try {
    host.addEventListener('click', (e) => {
      const t = e.target;
      if (!t || !t.tagName) return;
      const isVideo = t.tagName.toLowerCase() === 'video';
      if (!isVideo) return;
      const isFS = isFullscreenVideo(t);
      if (!isFS) return;
      e.preventDefault();
      try {
        // User interaction: clear any must-play or pause guards for this video
            try { if (window.__DEBUG_SLIDESHOW) console.debug('[SS] fullscreen video click toggle', { paused: t.paused }); } catch(_) {}
        try { if (window.__fsMustPlay) window.__fsMustPlay.delete(t); } catch(_) {}
        try { if (window.__fsPauseGuard) window.__fsPauseGuard.delete(t); } catch(_) {}
        // Mark this as a manual pause intent to avoid auto-resume
        try {
          if (!window.__fsManualPause) window.__fsManualPause = new WeakSet();
          if (!t.paused) window.__fsManualPause.add(t);
        } catch(_) {}
        if (t.paused) { t.play().catch(()=>{}); }
        else { t.pause(); }
      } catch(_) {}
    }, true);
  } catch(_) {}

  // Briefly show pause icon on hover when playing, only for active slide
  const overlayHideTimers = new WeakMap();
  const showPauseBriefly = (vp) => {
    const slide = vp && vp.closest('.swiper-slide');
    const overlay = vp && vp.querySelector('.video-overlay');
    const video = vp && vp.querySelector('video');
    if (!slide || !overlay || !video) return;
    if (!slide.classList.contains('swiper-slide-active')) return;
    if (video.paused) return; // paused shows play via other handlers
    overlay.classList.add('show-pause'); overlay.classList.remove('show-play');
    const prev = overlayHideTimers.get(vp);
    if (prev) { clearTimeout(prev); }
    const t = setTimeout(() => { try { overlay.classList.remove('show-pause'); } catch(_) {} }, 300);
    overlayHideTimers.set(vp, t);
  };
  host.addEventListener('pointerenter', (e) => {
    const vp = e.target && typeof e.target.closest === 'function' ? e.target.closest('.video-player-container') : null;
    if (!vp) return;
    // Only consider mouse/pen hover
    if (e.pointerType && e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
    showPauseBriefly(vp);
  }, true);
  host.addEventListener('pointermove', (e) => {
    const vp = e.target && typeof e.target.closest === 'function' ? e.target.closest('.video-player-container') : null;
    if (!vp) return;
    if (e.pointerType && e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
    showPauseBriefly(vp);
  }, true);
  host.addEventListener('pointerleave', (e) => {
    const vp = e.target && typeof e.target.closest === 'function' ? e.target.closest('.video-player-container') : null;
    if (!vp) return;
    const overlay = vp && vp.querySelector('.video-overlay');
    if (overlay) { overlay.classList.remove('show-pause'); }
    const prev = overlayHideTimers.get(vp);
    if (prev) { clearTimeout(prev); overlayHideTimers.delete(vp); }
  }, true);
}

function open(modalEl) {
  if (!modalEl) return;
  // Suppress immediate outside-click close from nav's global handler
  try { window.__suppressNextOutsideClickClose = true; } catch(_) {}
  if (!modalEl.dataset.built) { buildSlides(modalEl).catch(()=>{}); }
  else { try { if (typeof modalEl._recomputeLayout === 'function') modalEl._recomputeLayout(); } catch(_) {} }
  modalEl.classList.add('open');
  modalEl.setAttribute('aria-hidden', 'false');
  modalEl.setAttribute('aria-modal', 'true');
  // On open: scroll main content container to top to ensure consistent layout
  try {
    const pages = document.querySelector('.pages-container');
    if (pages && typeof pages.scrollTo === 'function') pages.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch(_) {}
  // Ensure first real slide is centered on open
  try {
    const sw = modalEl._swiper;
    if (sw) {
      requestAnimationFrame(() => {
        try {
          if (typeof sw.slideToLoop === 'function') sw.slideToLoop(0, 0, false);
          else if (typeof sw.slideTo === 'function') sw.slideTo(0, 0, false);
        } catch(_) {}
      });
    }
  } catch(_) {}
  // Bind per-modal resize listener once when opened
  try {
    if (modalEl._onWindowResize && !modalEl._resizeBound) {
      window.addEventListener('resize', modalEl._onWindowResize, { passive: true });
      modalEl._resizeBound = true;
    }
  } catch(_) {}
  // Reflect open state in nav label/hamburger
  try {
    const navChapters = document.querySelector('.nav-chapters-wrapper');
    const label = navChapters && navChapters.querySelector('.label');
    if (navChapters) navChapters.classList.add('open');
    if (label) label.textContent = 'CLOSE';
  } catch(_) {}
  try { modalEl.focus(); } catch(_) {}
}

function close(modalEl) {
  if (!modalEl) return;
  // Reset all videos to initial state when closing
  resetAllVideos(modalEl);
  // Unbind per-modal resize listener to avoid stacking across opens
  try {
    if (modalEl._onWindowResize && modalEl._resizeBound) {
      window.removeEventListener('resize', modalEl._onWindowResize);
      modalEl._resizeBound = false;
    }
  } catch(_) {}
  // Reset slides so the first slide is centered next time
  try {
    const allSlideshows = Array.from(document.querySelectorAll('.nav-dropdown.slideshow'));
    allSlideshows.forEach((el) => {
      const host = el.querySelector('.slideshow');
      if (!host) return;
      // If Swiper instance exists (desktop), reset to first real slide and recompute layout
      const sw = el._swiper;
      if (sw && typeof sw.slideToLoop === 'function') {
        try { sw.slideToLoop(0, 0, false); } catch(_) {}
        try { if (typeof el._recomputeLayout === 'function') el._recomputeLayout(); } catch(_) {}
      } else {
        // Mobile stacked: ensure scroll starts at top
        try { host.scrollTop = 0; } catch(_) {}
      }
    });
  } catch(_) {}
  modalEl.classList.remove('open');
  modalEl.setAttribute('aria-hidden', 'true');
  modalEl.setAttribute('aria-modal', 'false');
  // Reset nav label/hamburger
  try {
    const navChapters = document.querySelector('.nav-chapters-wrapper');
    const label = navChapters && navChapters.querySelector('.label');
    if (navChapters) navChapters.classList.remove('open');
    if (label) label.textContent = 'MENU';
  } catch(_) {}
}

const slideshowModalNew = {
  init() {
    const modals = Array.from(document.querySelectorAll('.nav-dropdown.slideshow'));
    // Inject nominee headings under each section title by reading from chapters dropdown
    try {
      const chapters = document.getElementById('chapters-dropdown');
      modals.forEach(m => {
        const id = m.getAttribute('data-id');
        const section = m.querySelector('.section-content');
        if (!id || !section) return;
        if (section.querySelector('.nominees')) return; // avoid duplicate injection
        if (!chapters) return;
        const chapterEl = chapters.querySelector(`.chapter[data-page-link="${id}"]`);
        const nomineeEl = chapterEl ? chapterEl.nextElementSibling : null;
        const hasNominee = nomineeEl && nomineeEl.classList && nomineeEl.classList.contains('nominee');
        if (!hasNominee) return;
        const nomineesEl = document.createElement('div');
        nomineesEl.className = 'nominees';
        // Replace <br> with a space and collapse remaining whitespace to a single space
        const html = nomineeEl.innerHTML || '';
        const normalized = html.replace(/<br\s*\/?>(?!\s)/gi, ' ');
        const cleaned = normalized.replace(/\s+/g, ' ').trim();
        nomineesEl.textContent = cleaned;
        const hTitle = section.querySelector('.slideshow-title');
        if (hTitle && hTitle.parentNode) {
          hTitle.parentNode.insertBefore(nomineesEl, hTitle.nextSibling);
        } else {
          section.insertBefore(nomineesEl, section.firstChild);
        }
      });
    } catch(_) {}
    const observer = new MutationObserver((muts) => {
      muts.forEach(mu => {
        if (mu.type === 'attributes' && mu.attributeName === 'class') {
          const el = mu.target;
          if (!el.classList.contains('open')) pauseAllVideos(el);
        }
      });
    });
    modals.forEach(m => observer.observe(m, { attributes: true }));

    window.openSlideshow = (id) => {
      try {
        const openEl = document.querySelector('.nav-dropdown.slideshow.open');
        if (openEl && openEl.getAttribute('data-id') !== id) {
          // Closing current chapter slideshow will reset _hasPlayedOnce and posters
          close(openEl);
        }
      } catch(_) {}
      const el = document.querySelector(`.nav-dropdown.slideshow[data-id="${id}"]`);
      if (el) open(el);
    };
    window.closeSlideshow = (id) => { const el = document.querySelector(`.nav-dropdown.slideshow[data-id="${id}"]`); if (el) close(el); };

    // Rebuild open slideshows on orientation changes (switch between stacked and swiper)
    const maybeRebuildOnOrientation = () => {
      try {
        // Avoid rebuilding during fullscreen playback to prevent churn
        if (document.body && document.body.classList && document.body.classList.contains('fs-playing')) return;
        const modalsOpen = Array.from(document.querySelectorAll('.nav-dropdown.slideshow.open'));
        if (!modalsOpen.length) return;
        const nowStack = shouldUseMobileStack();
        modalsOpen.forEach((modal) => {
          const prevStack = !!modal._mobileStack;
          if (prevStack !== nowStack) {
            try {
              const sw = modal._swiper;
              if (sw && typeof sw.destroy === 'function') sw.destroy(true, true);
            } catch(_) {}
            try { modal._swiper = null; } catch(_) {}
            try { const host = modal.querySelector('.slideshow'); if (host) host.innerHTML = ''; } catch(_) {}
            try { modal.dataset.built = ''; modal.dataset.ready = ''; } catch(_) {}
            buildSlides(modal).catch(()=>{});
          } else {
            try { if (typeof modal._recomputeLayout === 'function') modal._recomputeLayout(); } catch(_) {}
          }
        });
      } catch(_) {}
    };
    let _orientTimer = 0;
    const debounced = () => { if (_orientTimer) { try { clearTimeout(_orientTimer); } catch(_) {} } _orientTimer = setTimeout(maybeRebuildOnOrientation, 140); };
    try { window.addEventListener('orientationchange', debounced, { passive: true }); } catch(_) {}
    try {
      if (window.matchMedia) {
        const mq = window.matchMedia('(orientation: portrait)');
        if (typeof mq.addEventListener === 'function') mq.addEventListener('change', debounced);
        else if (typeof mq.addListener === 'function') mq.addListener(debounced);
      }
    } catch(_) {}
    try { window.addEventListener('resize', debounced, { passive: true }); } catch(_) {}
  }
};

export default slideshowModalNew;

// Tooltip + hover/tap bindings for landing page SVG hotspots
let tooltipEl = null;
let tooltipTarget = null;
let touchArmedTarget = null;

function ensureTooltip() {
  if (tooltipEl) return tooltipEl;
  const el = document.createElement('div');
  el.className = 'svg-tooltip hidden';
  document.body.appendChild(el);
  tooltipEl = el;
  return el;
}

function formatLabelFromId(id) {
  if (!id) return '';
  const base = id.replace(/[-_]+/g, ' ').trim().toUpperCase();
  if (id === 'best-makeup-and-hairstyling') return 'BEST MAKEUP\nAND HAIRSTYLING';
  if (id === 'best-costume-design') return 'BEST COSTUME\nDESIGN';
  return base;
}

function showTooltipFor(el, text) {
  const t = ensureTooltip();
  t.textContent = text;
  t.classList.remove('hidden');
  const rect = el.getBoundingClientRect();
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;
  t.style.left = x + 'px';
  t.style.top = y + 'px';
  t.style.transform = 'translate(-50%, -50%)';
  tooltipTarget = el;
}

function hideTooltip() {
  if (!tooltipEl) return;
  tooltipEl.classList.add('hidden');
  tooltipTarget = null;
}

// Bind hover/tap interactions to inline SVG paths in #cropped-images
(function bindSvgHotspots(){
  try {
    const modals = Array.from(document.querySelectorAll('.nav-dropdown.slideshow'));
    if (!modals.length) return;
    modals.forEach((m) => {
      const id = m.getAttribute('data-id');
      if (!id) return;
      const selector = [
        `#cropped-images [id="${id}"]`,
        `#cropped-images [data-id="${id}"]`,
        `#cropped-images [data-target="${id}"]`,
        `#cropped-images [data-link="${id}"]`,
        `#cropped-images [data-page-link="${id}"]`
      ].join(', ');
      const svgTargets = Array.from(document.querySelectorAll(selector));
      if (!svgTargets.length) return;
      const label = formatLabelFromId(id);
      svgTargets.forEach((svgTarget) => {
        svgTarget.style.cursor = 'pointer';
        svgTarget.style.pointerEvents = 'auto';
        svgTarget.setAttribute('role', 'button');
        svgTarget.setAttribute('tabindex', '0');
        svgTarget.setAttribute('aria-label', `Open ${label} slideshow`);

        // Show tooltip only for non-rect shapes
        const isRect = (svgTarget.tagName || '').toLowerCase() === 'rect';
        if (!isRect) {
          svgTarget.addEventListener('mouseenter', () => showTooltipFor(svgTarget, label));
          svgTarget.addEventListener('mouseleave', () => hideTooltip());
        }

        const armForSecondTap = () => {
          showTooltipFor(svgTarget, label);
          svgTarget.dataset.tapArmed = '1';
          svgTarget.dataset.suppressNextClick = '1';
          touchArmedTarget = svgTarget;
          svgTarget.classList.add('tapped');
        };

        svgTarget.addEventListener('pointerdown', (e) => {
          svgTarget.dataset.lastPointer = e.pointerType || '';
          if (e.pointerType === 'touch') {
            // For rects, open immediately on touch (no two-tap tooltip flow)
            if (isRect) {
              hideTooltip();
              window.openSlideshow(id);
              svgTarget.dataset.openedOnPointer = '1';
              if (touchArmedTarget === svgTarget) touchArmedTarget = null;
              svgTarget.classList.remove('tapped');
              return;
            }
            if (svgTarget.dataset.tapArmed === '1') {
              hideTooltip();
              window.openSlideshow(id);
              svgTarget.dataset.tapArmed = '';
              svgTarget.dataset.suppressNextClick = '';
              svgTarget.dataset.openedOnPointer = '1';
              if (touchArmedTarget === svgTarget) touchArmedTarget = null;
              svgTarget.classList.remove('tapped');
            } else {
              armForSecondTap();
            }
          }
        });

        svgTarget.addEventListener('click', (e) => {
          if (svgTarget.dataset.openedOnPointer === '1') {
            e.preventDefault();
            svgTarget.dataset.openedOnPointer = '';
            return;
          }
          if (svgTarget.dataset.lastPointer === 'touch' && svgTarget.dataset.tapArmed === '1' && svgTarget.dataset.suppressNextClick === '1') {
            e.preventDefault();
            svgTarget.dataset.suppressNextClick = '';
            return;
          }
          if (!isRect) hideTooltip();
          window.openSlideshow(id);
          svgTarget.dataset.tapArmed = '';
          svgTarget.dataset.suppressNextClick = '';
          if (touchArmedTarget === svgTarget) touchArmedTarget = null;
          svgTarget.classList.remove('tapped');
        });

        svgTarget.addEventListener('keydown', (e) => {
          if (!['Enter',' '].includes(e.key)) return;
          e.preventDefault();
          if (!isRect) hideTooltip();
          window.openSlideshow(id);
          svgTarget.classList.remove('tapped');
        });
      });
    });
  } catch(_) {}
})();

// Global touch handler to clear armed/tapped state when tapping elsewhere
document.addEventListener('pointerdown', (e) => {
  if (e.pointerType !== 'touch') return;
  if (!touchArmedTarget) return;
  const t = touchArmedTarget;
  if (e.target === t || (typeof t.contains === 'function' && t.contains(e.target))) return;
  t.dataset.tapArmed = '';
  t.dataset.suppressNextClick = '';
  if (tooltipTarget === t) hideTooltip();
  touchArmedTarget = null;
  try { t.classList.remove('tapped'); } catch(_) {}
}, { capture: true });

// Hide the SVG tooltip when the user scrolls (desktop or mobile)
(() => {
  try {
    const onScrollHide = () => { try { hideTooltip(); } catch(_) {} };
    // Window-level scroll (fallback)
    window.addEventListener('scroll', onScrollHide, { passive: true });
    // Primary content container scroll
    const pages = document.querySelector('.pages-container');
    if (pages) pages.addEventListener('scroll', onScrollHide, { passive: true });
    // Also hide on wheel/touchmove to be responsive to user movement
    window.addEventListener('wheel', onScrollHide, { passive: true });
    window.addEventListener('touchmove', onScrollHide, { passive: true });
  } catch(_) {}
})();
